<script>
	import Router, { link, location as routerLocation } from "svelte-spa-router";
    import { push, pop, replace } from 'svelte-spa-router';
	import { Policy, Language } from "@Store";
    import translations from "../utils/translations.js";

    // Build translation map and helper
    const translationMap = {};
    translations.forEach(item => {
        if (item.eng && item.fr) translationMap[item.eng] = item.fr;
    });
    $: t = (text) => ($Language === 'fr' && translationMap[text]) ? translationMap[text] : text;
    import { writable } from "svelte/store"
    import { onMount, createEventDispatcher, setContext, getContext, tick } from "svelte";
    import { to } from "@API";
    import { validateApplicant, 
        validateEstablishment, 
        validatePolicy, 
        validatePropertyDetails, 
        validateMedMal, 
        validateClaims,
        skipPropertyDetails,
        skipMedMal
    } from "@helpers/validator";
    import Applicant from "@routes/Applicant.svelte";
    import Establishment from "@routes/Establishment.svelte";
    import PolicyPage from "@routes/Policy.svelte";
    import PropertyDetails from "@routes/PropertyDetails.svelte";
    import MedMal from "@routes/MedMal.svelte";
    import Claims from "@routes/Claims.svelte";
    import Declaration from "@routes/Declaration.svelte";
    import Summary from "@routes/Summary.svelte";
    import Checkout from "@routes/Checkout.svelte";
    import Complete from "@routes/Complete.svelte";
    const dispatch = createEventDispatcher();

    let policyID = writable(localStorage.getItem('policyID'))

    let routes = {
        "/applicant": Applicant,
        "/establishment": Establishment,
        "/policy": PolicyPage,
        "/additionalinsureds": PropertyDetails,
        "/medmal": MedMal,
        "/claims": Claims,
        "/declaration": Declaration,
        "/summary": Summary,
        "/checkout": Checkout,
        "/complete": Complete,
    }

    $: prefix = $Policy?.uuid ? `/${$Policy.uuid}` : `/${$policyID}`;

    let saveTimers = {};
    let saveSeq = {};

    // NEW: robust batching
    let dirtyQIDs = new Set();
    let saveTimer = null;
    let batchSeq = 0;

    function scheduleSaveAll() {
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(saveAllDirty, 400);
    }

    async function saveAllDirty() {
        if (dirtyQIDs.size === 0 || !$Policy?.uuid) return;
        const ids = Array.from(dirtyQIDs);
        dirtyQIDs.clear();
        batchSeq += 1;
        const mySeq = batchSeq;

        // mark all as saving
        CONTEXT.savingQuestions.update(s => {
            const n = { ...s };
            ids.forEach(id => n[id] = true);
            return n;
        });

        let [error, response] = await to(Insurecert.API.policy($Policy.uuid).save({
            answers: $Policy.answers
        }));

        // If a newer batch started, don't apply stale response
        if (mySeq === batchSeq && response) {
            const updatedAnswers = { ...$Policy.answers };
            ids.forEach(id => {
                if (response.answers && response.answers[id]) {
                    updatedAnswers[id] = response.answers[id];
                }
            });

            $Policy = {
                ...$Policy,
                answers: updatedAnswers,
                premium: response.premium ?? $Policy.premium,
                tax_total: response.tax_total ?? $Policy.tax_total,
                service_fee: response.service_fee ?? $Policy.service_fee,
                subtotal: response.subtotal ?? $Policy.subtotal,
                status: response.status ?? $Policy.status,
                valid: response.valid ?? $Policy.valid,
                validation_result: response.validation_result ?? $Policy.validation_result,
                saved_named_values: response.saved_named_values ?? $Policy.saved_named_values
            };
        }

        // clear saving flags
        CONTEXT.savingQuestions.update(s => {
            const n = { ...s };
            ids.forEach(id => delete n[id]);
            return n;
        });
    }

    // Create the validation store
    let routeValidation = writable({
        applicant: false,
        establishment: false,
        policy: false,
        additionalinsureds: false,
        medmal: false,
        claims: false,
    });

    function invalidatePolicy() {
        if (!$Policy) return false;
        console.log('11 Policy >>>>>>>>>>>>>', $Policy);
        const validationState = {
            applicant: validateApplicant($Policy),
            establishment: validateEstablishment($Policy),
            policy: validatePolicy($Policy),
            additionalinsureds: validatePropertyDetails($Policy),
            medmal: validateMedMal($Policy),
            claims: validateClaims($Policy),
        };
        console.log('33 validationState >>>>>>>>>>>>>', validationState);

        routeValidation.set(validationState);

        const allValid = Object.values(validationState).every(Boolean);
        if ($Policy.valid !== allValid) {
            $Policy = { ...$Policy, valid: allValid };
        }
        console.log('22 Policy >>>>>>>>>>>>>', $Policy);
        return allValid;
    }

    let CONTEXT = {
        Answers: writable({}),
        policyID,
        routeValidation,
        savingQuestions: writable({}) // NEW: per-question saving indicator
    }

    const { savingQuestions } = CONTEXT;

    // Set context so child components can access it
    setContext('CONTEXT', CONTEXT);

    // Navigation helper
    $: currentRoute = $routerLocation?.split('/').pop() || 'applicant';
    $: isFirstPage = currentRoute === 'applicant';
    
    // Access the validation store properly
    $: canGoNext = $routeValidation[currentRoute] !== false;
    
    $: isSaving = Object.keys($savingQuestions).length > 0;
    
    $: routeOrder = [
        'applicant',
        'establishment',
        'policy',
        'additionalinsureds',
        ...(skipMedMal($Policy) ? [] : ['medmal']),
        'claims',
        'declaration',
        'summary',
        'checkout',
        'complete'
    ];
    
    async function handleBack() {
        await saveAllDirty();
        const currentIndex = routeOrder.indexOf(currentRoute);
        if (currentIndex > 0) {
            replace(`/${$Policy.uuid}/${routeOrder[currentIndex - 1]}`);
            setTimeout(() => {
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
            }, 0);
        }
    }

    async function handleNext() {
        await saveAllDirty();
        const currentIndex = routeOrder.indexOf(currentRoute);
        if (currentIndex < routeOrder.length - 1) {
            replace(`/${$Policy.uuid}/${routeOrder[currentIndex + 1]}`);
            setTimeout(() => {
                window.scrollTo(0, 0);
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
            }, 0);
        }
    }

    const uuidPattern = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

    function parseHash() {
        const parts = window.location.hash.replace(/^#\/?/, '').split('/');
        return {
            uuid: parts[0] && uuidPattern.test(parts[0]) ? parts[0] : null,
            step: parts[1] || null
        };
    }

    async function loadPolicyByUUID(uuid) {
        let [error, response] = await to(Insurecert.API.policy(uuid).get());
        if (response) {
            $policyID = uuid;
            localStorage.setItem('policyID', uuid);
            $Policy = response;
            CONTEXT.Answers.set(JSON.parse(JSON.stringify($Policy.answers)));
            return true;
        }
        return false;
    }

    function enforceRouteRules() {
        const { uuid, step } = parseHash();
        if (!$Policy?.uuid || !uuid || uuid !== $Policy.uuid) return;

        // Completed policies must only show the complete page
        if ($Policy.status === 'completed' && step !== 'complete') {
            replace(`/${$Policy.uuid}/complete`);
        }
        // if a policy is unpaid, dont let them go back and edit answers
        if ($Policy.status === 'unpaid') {
            if (!['summary', 'checkout'].includes(step)) {
                replace(`/${$Policy.uuid}/summary`);
            }
        }
    }

    async function handleHashChange() {
        const { uuid } = parseHash();
        if (uuid && uuid !== $Policy?.uuid) {
            const ok = await loadPolicyByUUID(uuid);
            if (ok) {
                await startPolicy();
                enforceRouteRules();
                return;
            }
        } else {
            enforceRouteRules();
        }
    }

    function clearNewFlagFromURL() {
        const url = new URL(window.location.href);
        let changed = false;
        // Clear from regular query params
        if (url.searchParams.has('new')) {
            url.searchParams.delete('new');
            changed = true;
        }
        // Clear from inside hash fragment
        const hash = url.hash || '';
        const hashQueryIndex = hash.indexOf('?');
        if (hashQueryIndex !== -1) {
            const hashPath = hash.substring(0, hashQueryIndex);
            const hashParams = new URLSearchParams(hash.substring(hashQueryIndex));
            hashParams.delete('new');
            const remaining = hashParams.toString();
            url.hash = remaining ? `${hashPath}?${remaining}` : hashPath;
            changed = true;
        }
        if (changed) {
            history.replaceState({}, '', url.toString());
        }
    }

    function isNewPolicyRequested() {
        // Check regular query params (before #): ?new=true
        const search = new URLSearchParams(window.location.search);
        if (search.get('new') === 'true') return true;
        // Check inside hash fragment: #/?new=true or #/something?new=true
        const hash = window.location.hash || '';
        const hashQueryIndex = hash.indexOf('?');
        if (hashQueryIndex !== -1) {
            const hashParams = new URLSearchParams(hash.substring(hashQueryIndex));
            if (hashParams.get('new') === 'true') return true;
        }
        return false;
    }

    onMount(async () => {
        window.addEventListener('hashchange', handleHashChange);

        if (isNewPolicyRequested()) {
            localStorage.removeItem('policyID');

            let [err, resp] = await to(Insurecert.API.policy().new({}));
            if (resp) {
                $policyID = resp.uuid.split(' ').join('');
                localStorage.setItem('policyID', resp.uuid);
                $Policy = resp;
                CONTEXT.Answers.set(JSON.parse(JSON.stringify(resp.answers)));

                prefix = `/${resp.uuid}`;
                clearNewFlagFromURL();
                replace(`/${resp.uuid}/applicant`);
                return;
            }
        }

        // 1) If URL has a UUID, load it and ignore cookie
        const { uuid, step } = parseHash();
        if (uuid) {
            const ok = await loadPolicyByUUID(uuid);
            if (ok) {
                // navigate to step if provided; Router will use updated prefix
                // if (step) replace(`/${uuid}/${step}`);
                // else await startPolicy();
                await startPolicy();
                enforceRouteRules();
                return;
            } else {
                // bad uuid -> start new
                localStorage.removeItem('policyID');
            }
        }

        // 2) Fallback: cookie policy
        if ($policyID) {
            let [error, response] = await to(Insurecert.API.policy($policyID).get());
            if (response) {
                $Policy = response;
                CONTEXT.Answers.set(JSON.parse(JSON.stringify($Policy.answers)));
                await startPolicy();
                enforceRouteRules();
                return;
            } else {
                localStorage.removeItem('policyID');
            }
        }

        // 3) No UUID anywhere -> new policy
        let [err, resp] = await to(Insurecert.API.policy().new({}));
        if (resp) {
            $policyID = resp.uuid.split(' ').join('');
            localStorage.setItem('policyID', resp.uuid);
            $Policy = resp;
            CONTEXT.Answers.set(JSON.parse(JSON.stringify(resp.answers)));
            prefix = `/${resp.uuid}`; 
            replace(`/${resp.uuid}/applicant`);
            return;
        }

        const vis = async () => {
            if (document.visibilityState === 'hidden') {
                await saveAllDirty();
            }
        };
        document.addEventListener('visibilitychange', vis);
        return () => document.removeEventListener('visibilitychange', vis);
    });

    async function startPolicy() {
        invalidatePolicy();
        let [validationError,validPolicy] = await to(Insurecert.API.policy($Policy.uuid).validate());

        if(validationError){
            $Policy.validation_result = JSON.stringify(validationError.data);
            // replace(`/${$Policy.uuid}/summary`);
            // replace(`/${$Policy.uuid}/checkout`);
            replace(`/${$Policy.uuid}/applicant`);
            // replace(`/${$Policy.uuid}/operations`);
            // replace(`/${$Policy.uuid}/medicalmalpractice`);
            // replace(`/${$Policy.uuid}/officepackage`);
            // replace(`/${$Policy.uuid}/directorsandofficersliability`);
            // replace(`/${$Policy.uuid}/legalexpense`);
            // replace(`/${$Policy.uuid}/cyber`);
            // replace(`/${$Policy.uuid}/priorinsuranceandclaims`);
        } else {
            if($Policy.status == 'completed'){
                replace(`/${$Policy.uuid}/complete`);
            }else{
                // THIS IS IF POLICY IS VALID
                replace(`/${$Policy.uuid}/declaration`);
                // replace(`/${$Policy.uuid}/summary`);
                // replace(`/${$Policy.uuid}/applicant`);
                // replace(`/${$Policy.uuid}/operations`);
                // replace(`/${$Policy.uuid}/medicalmalpractice`);
                // replace(`/${$Policy.uuid}/officepackage`);
                // replace(`/${$Policy.uuid}/directorsandofficersliability`);
                // replace(`/${$Policy.uuid}/legalexpense`);
                // replace(`/${$Policy.uuid}/cyber`);
                // replace(`/${$Policy.uuid}/priorinsuranceandclaims`);
            }
        }
    }

    CONTEXT.handleAnswer = handleAnswer;
    async function handleAnswer(e) {
        const qid = e.questionId;

        // Optimistic local update (instant UI)
        let answer = {
            choice: e.choice || null,
            text_answer: e.text_answer || '',
            other: e.other || '',
            question: qid,
            choices: e.choices || []
        };
        $Policy.answers[qid] = answer;
        CONTEXT.Answers.set(JSON.parse(JSON.stringify($Policy.answers)));

        // NEW: mark dirty and schedule a single batched save
        dirtyQIDs.add(qid);
        scheduleSaveAll();
    }

     // NEW: Handle effective date updates
    CONTEXT.handleEffectiveDateUpdate = handleEffectiveDateUpdate;
    async function handleEffectiveDateUpdate(e) {

        if (!$Policy?.uuid) return;
        
        const { start_date, end_date } = e.detail;

        // Update the policy store immediately for UI responsiveness
        $Policy = {
            ...$Policy,
            start_date: start_date,
            end_date: end_date
        };

        // Save to backend
        let [error, response] = await to(Insurecert.API.policy($Policy.uuid).save({
            start_date: start_date,
            end_date: end_date
        }));


        if (response) {
            $Policy = {
                ...$Policy,
                ...response
            };
        }
        if (error) {
            console.error('Failed to update effective dates:', error);
        }
    }

    CONTEXT.handleUpdateDeclaration = handleUpdateDeclaration;
    async function handleUpdateDeclaration(e) {
        let [error,response] = await to(Insurecert.API.policy($Policy.uuid).save({
            declaration: e
        }));

        if (response){
            $Policy = response;
        }
    }
</script>

<div class="app-container">
    <div class="background-orbs">
        <div class="orb orb-green-1"></div>
        <div class="orb orb-green-2"></div>
        <div class="orb orb-gold-1"></div>
        <div class="orb orb-gold-2"></div>
        <div class="orb orb-magenta"></div>
        <div class="orb orb-green-light"></div>
    </div>

    {#if $Policy}
        <div class="content">
            <Router {routes} {prefix} />
        </div>
        {#if !['declaration', 'checkout', 'complete'].includes(currentRoute)}
            <footer class="nav-footer">
                <div class="footer-container">
                    <button 
                        class="nav-button back-button" 
                        disabled={isFirstPage || isSaving}
                        on:click={handleBack}
                    >
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        {t("Back")}
                    </button>

                    <div class="center-section">
                        <div class="route-indicator">
                            {#each routeOrder.slice(0, routeOrder.length - 2) as route, index}
                                <span class="dot" class:active={route === currentRoute}></span>
                            {/each}
                        </div>
                        {#if !canGoNext && !isSaving}
                            <span class="fields-hint">{t("All fields must be completed in order to proceed")}</span>
                        {/if}
                    </div>

                    <button
                        class="nav-button next-button"
                        disabled={!canGoNext || isSaving}
                        on:click={handleNext}
                    >
                        {t("Next")}
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </footer>
        {/if}
    {/if}
</div>

<style>
    .app-container {
        display: flex;
        flex-direction: column;
    }

    .content {
        flex: 1;
        padding-bottom: 100px; 
    }

    .nav-footer {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(20px) saturate(180%);
        -webkit-backdrop-filter: blur(20px) saturate(180%);
        border-top: 0.5px solid rgba(0, 0, 0, 0.1);
        z-index: 100;
    }

    .footer-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 1rem 2rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 2rem;
    }

    .nav-button {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.75rem 1.5rem;
        font-size: 1rem;
        font-weight: 500;
        border: none;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        min-width: 100px;
    }

    .back-button {
        background: transparent;
        color: var(--base-green);
    }

    .back-button:hover:not(:disabled) {
        background: rgba(172, 212, 172, 0.3);
    }

    .back-button:disabled {
        color: #d2d2d7;
        cursor: not-allowed;
    }

    .next-button {
        background: var(--base-green);
        color: white;
    }

    .next-button:hover {
        background: var(--base-green-light);
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(172, 212, 172, 0.3);
    }

    .next-button:disabled {
        background: #d2d2d7;
        cursor: not-allowed;
    }

    .next-button:active {
        transform: translateY(0);
    }

    .center-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.35rem;
    }

    .route-indicator {
        display: flex;
        gap: 0.5rem;
        align-items: center;
    }

    .fields-hint {
        font-size: 0.9rem;
        color: var(--base-green);
        font-weight: 500;
        letter-spacing: -0.01em;
        text-align: center;
        animation: fadeIn 0.3s ease;
    }

    @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-4px); }
        to { opacity: 1; transform: translateY(0); }
    }

    .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #d2d2d7;
        transition: all 0.3s ease;
    }

    .dot.active {
        background: var(--base-green);
        transform: scale(1.25);
    }

    .saving-indicator {
        margin: -0.5rem 0 0.75rem;
        color: #86868b;
        font-size: 0.875rem;
        font-weight: 500;
    }

    @media (max-width: 768px) {
        .footer-container {
            padding: 1rem;
        }

        .nav-button {
            padding: 0.625rem 1.25rem;
            font-size: 0.9rem;
            min-width: 80px;
        }
    }


.background-orbs {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: -1;
    overflow: hidden;
}

.orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(30px);
    opacity: 0.15;
    animation: float 15s ease-in-out infinite;
}

.orb-green-1 {
    width: 400px;
    height: 400px;
    background: var(--base-green);
    top: -100px;
    left: -100px;
    animation: float 18s ease-in-out infinite;
}

.orb-green-2 {
    width: 350px;
    height: 350px;
    background: var(--base-green-light);
    bottom: -50px;
    right: 10%;
    animation: float 22s ease-in-out infinite reverse;
    animation-delay: 5s;
}

.orb-gold-1 {
    width: 450px;
    height: 450px;
    background: #F05C22;  /* Orange */
    top: 20%;
    right: -150px;
    animation: float 20s ease-in-out infinite;
    animation-delay: 2s;
}

.orb-gold-2 {
    width: 300px;
    height: 300px;
    background: #F05C22;  /* Orange */
    bottom: 15%;
    left: 15%;
    animation: float 16s ease-in-out infinite reverse;
    animation-delay: 7s;
}

.orb-green-light {
    width: 380px;
    height: 380px;
    background: var(--base-green-lightest);  /* Lightest Teal */
    top: 50%;
    left: -120px;
    animation: float 19s ease-in-out infinite;
    animation-delay: 3s;
}

.orb-magenta {
    width: 320px;
    height: 320px;
    background: #B01C87;  /* PANTONE 241 CP - Magenta */
    top: 35%;
    right: 5%;
    animation: float 17s ease-in-out infinite;
    animation-delay: 4s;
}

@keyframes float {
    0%, 100% {
        transform: translate(0, 0) scale(1);
    }
    25% {
        transform: translate(80px, -100px) scale(1.05);
    }
    50% {
        transform: translate(-100px, 80px) scale(0.95);
    }
    75% {
        transform: translate(80px, 250px) scale(1.08);
    }
}

@media (max-width: 768px) {
    .orb {
        filter: blur(60px);
        opacity: 0.1;
    }
    
    .orb-green-1, .orb-gold-1 {
        width: 300px;
        height: 300px;
    }
    
    .orb-green-2, .orb-green-light {
        width: 250px;
        height: 250px;
    }
    
    .orb-gold-2 {
        width: 200px;
        height: 200px;
    }
}
</style>